import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { listBatches, archiveBatch } from '../../../api/batches';
import { fetchBranches } from '../../../api/branches';
import { useNavigate } from 'react-router-dom';
import ExcelDownloadButton from '../../../components/common/ExcelDownloadButton';
import { getBatchListExportColumns } from '../../../utils/listExportColumns';
import BatchTable from '../../../components/batches/BatchTable';
import PaginationFooter from '../../../components/common/PaginationFooter';
import useSortedClientPagination from '../../../hooks/useSortedClientPagination';
import { batchListSortColumns } from '../../../config/tableSortColumns';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
} from '../../../components/layout/ListPageLayout';
import { showConfirm } from '../../../utils/popup';

function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const AdminBatchList = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const navigate = useNavigate();

    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    const [branchFilter, setBranchFilter] = useState('all');
    const [branches, setBranches] = useState([]);

    const load = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const [batchesRes, branchesRes] = await Promise.all([
                listBatches('ADMIN', { page: 0, size: 200 }),
                fetchBranches(),
            ]);
            setRows(batchesRes.data?.content ?? batchesRes.data ?? []);
            setBranches(branchesRes.data?.content ?? branchesRes.data ?? []);
        } catch {
            setRows([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onEdit = (row) => navigate(`/admin/batches/${row.batchId || row.id}/edit`);
    const onArchive = async (row) => {
        if (!await showConfirm(t('batches.archiveConfirm'))) return;
        try {
            await archiveBatch('ADMIN', row.batchId || row.id);
            load();
        } catch {
            alert(t('batches.archiveFailed'));
        }
    };
    const onSessions = (row) => navigate(`/admin/batches/${row.batchId || row.id}/sessions`);

    const filtered = React.useMemo(() => {
        let result = rows;
        if (branchFilter !== 'all') {
            const targetBranchId = parseInt(branchFilter, 10);
            result = rows.filter((r) => (r.branchId ?? null) === targetBranchId);
        }

        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => String(x ?? '').toLowerCase();
            result = result.filter((r) => {
                const hay = [r.name, r.id, r.batchId, r.levelName, r.levelId].map(pick).join(' ');
                return term.split(/\s+/).every((tok) => hay.includes(tok));
            });
        }

        return result;
    }, [rows, dq, branchFilter]);
    const shown = filtered.length;
    const total = filtered.length;

    const pagination = useSortedClientPagination(filtered, {
        columns: batchListSortColumns,
        resetKey: `${branchFilter}-${dq}`,
    });

    const branchTabs = useMemo(() => {
        const tabs = [{ key: 'all', label: t('common.all'), count: rows.length }];
        branches.forEach((branch) => {
            const branchId = branch.id ?? branch.branchId;
            const branchName = branch.name ?? branch.branchName ?? `${t('batches.branch')} ${branchId}`;
            const count = rows.filter((r) => (r.branchId ?? null) === branchId).length;
            tabs.push({ key: String(branchId), label: branchName, count });
        });
        return tabs;
    }, [rows, branches, t]);

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('batches.title')}
                subtitle={t('batches.subtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getBatchListExportColumns(t)}
                            data={filtered}
                            filename="batches"
                            disabled={filtered.length === 0}
                        />
                        <button type="button" onClick={() => navigate('/admin/batches/new')} className="btn-page-action-primary whitespace-nowrap">
                            <svg className="w-5 h-5 mr-2 -ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            {t('batches.add')}
                        </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={branchTabs} active={branchFilter} onChange={setBranchFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('batches.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('batches.entityLabel')}
            />

            {loadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.listLoadFailed')}
                </div>
            )}
            {loading ? (
                <ListLoadingSpinner />
            ) : (
                <>
                    <BatchTable
                        role="ADMIN"
                        rows={pagination.pageItems}
                        loading={loading}
                        loadError={loadError}
                        onEdit={onEdit}
                        onArchive={onArchive}
                        onSessions={onSessions}
                    />

                    {filtered.length > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </>
            )}
        </ListPageLayout>
    );
};

export default AdminBatchList;
